import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { ChevronDown } from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DealViewMetrics = () => {
  const [selectedDeal, setSelectedDeal] = useState('all');
  const [deals] = useState([
    { id: 'all', name: 'All Deals' },
    { id: 'deal1', name: 'Summer Sale 2023' },
    { id: 'deal2', name: 'Flash Deals' },
    { id: 'deal3', name: 'Xmas Promo' },
  ]);

  const [durations] = useState([
    "weekly",
    "monthly",
    "quarterly"
  ]);
  const [selectedDuration, setSelectedDuration]= useState("monthly");

  const weeklyLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MonthlyLabels =  ['Jan', 'Feb', 'Mar', 'Apr', 'Jun', 'Jul', 'Aug',"Sept","Oct","Nov","Dec"]
  const QuarterlyLabels = ['Q1', 'Q2', 'Q3', 'Q4']

  // Sample data for each weekly, Monthly and Quarterly labels
  const dealData = {
    all: {
      weekly: [65, 75, 45, 85, 25, 50, 70],
      monthly: [60, 70, 80, 90, 50, 40, 30, 20, 10, 15, 25, 35],
      quarterly: [75, 85, 95, 65],
    },
    deal1: {
      weekly: [65, 75, 45, 85, 25, 50, 70],
      monthly: [70, 80, 60, 90, 50, 40, 30, 20, 10, 15, 25, 35],
      quarterly: [80, 90, 70, 60],
    },
    deal2: {
      weekly: [45, 60, 30, 70, 40, 55, 80],
      monthly: [50, 60, 40, 70, 30, 20, 10, 15, 25, 35, 45, 55],
      quarterly: [60, 70, 50, 40],
    },
    deal3: {
      weekly: [55, 35, 65, 40, 60, 30, 50],
      monthly: [40, 50, 30, 60, 20, 10, 15, 25, 35, 45, 55, 65],
      quarterly: [50, 60, 40, 30],
    },
  };

  const chartData = {
    labels: selectedDuration === "weekly" ? weeklyLabels : selectedDuration === "monthly" ? MonthlyLabels : QuarterlyLabels,
    datasets: [
      {
        label: 'CTR',
        data: dealData[selectedDeal][selectedDuration],
        borderColor: '#FF6B6B',
        backgroundColor: 'rgba(255, 107, 107, 0.1)',
        tension: 0.4,
        fill: true,
      }
    ]
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          }
        },
        grid: {
          display: false
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          }
        },
        grid: {
          display: false
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };


  return (
        <div className="col-span-12 p-4 bg-white rounded-2xl shadow-xl lg:col-span-8 md:p-6">
          <div className="flex flex-col gap-4 justify-between mb-6 md:flex-row md:items-center">
            <h2 className="font-semibold text-gray-800 text-md md:text-sm">Deal View</h2>
            <div className="flex flex-wrap gap-3">
              <div className="relative md:flex-none">
                <select
                  value={selectedDeal}
                  onChange={(e) => setSelectedDeal(e.target.value)}
                  className="w-full md:w-auto appearance-none pl-4 pr-10 py-2.5 text-sm  rounded-xl border-2 border-pink-100 
                    text-gray-700 font-medium hover:border-pink-200 focus:border-pink-300 focus:ring-2 
                    focus:ring-pink-100 focus:outline-none transition-all cursor-pointer min-w-[120px] max-w-[120px] bg-white
                    shadow-sm hover:shadow-md"
                >
                  {deals.map((deal) => (
                    <option 
                      key={deal.id} 
                      value={deal.id}
                      className="py-2 text-gray-700"
                    >
                      {deal.name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown size={16} className="text-pink-400" />
                </div>
                <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-b from-pink-400 to-pink-500 rounded-l-xl" />
              </div>
              <div className="relative flex-1 md:flex-none">
                <select
                  value={selectedDuration}
                  onChange={(e) => setSelectedDuration(e.target.value)}
                  className="w-full md:w-auto appearance-none pl-4 pr-10 py-2.5 text-sm bg-white rounded-xl border-2 border-pink-100 
                    text-gray-700 font-medium hover:border-pink-200 focus:border-pink-300 focus:ring-2 
                    focus:ring-pink-100 focus:outline-none transition-all cursor-pointer min-w-[120px] max-w-[120px]
                    shadow-sm hover:shadow-md"
                >
                  {durations.map((period, index) => (
                    <option 
                      key={index} 
                      value={period}
                      className="py-2 text-gray-700"
                    >
                      {period}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown size={16} className="text-pink-400" />
                </div>
                <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-b from-pink-400 to-pink-500 rounded-l-xl" />
              </div>
            </div>
          </div>
          <div className="h-[250px] md:h-[400px]">
            <Line 
              data={chartData} 
              options={options}
            />
          </div>
        </div>

  );
};

export default DealViewMetrics;